import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight, MailCheck } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // The API always returns 200 (no account enumeration), so success here
      // just means the request was accepted — we show the same message either way.
      await axios.post(`${API_URL}/api/auth/forgot-password`, { email }, { withCredentials: true });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-midnight flex items-center justify-center p-4 sm:p-8 relative">
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-aura-purple/20 rounded-full blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md relative z-10"
      >
        <Link to="/" className="flex items-center gap-3 mb-8 sm:mb-12">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-aura-purple to-aura-gold flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-2xl font-serif font-medium text-text-light">Memouraa</span>
        </Link>

        {sent ? (
          <div className="text-center">
            <div className="w-14 h-14 rounded-full bg-champagne/10 flex items-center justify-center mx-auto mb-5">
              <MailCheck className="w-7 h-7 text-champagne" />
            </div>
            <h1 className="text-3xl font-serif font-medium text-text-light mb-3">Check your email</h1>
            <p className="text-text-muted mb-8">
              If an account exists for <span className="text-text-light">{email}</span>, we&rsquo;ve sent a
              link to reset your password. It expires in 60 minutes.
            </p>
            <Link to="/login" className="text-champagne font-medium hover:underline">
              Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <h1 className="text-3xl sm:text-4xl font-serif font-medium text-text-light mb-2">
              Reset your password
            </h1>
            <p className="text-text-muted mb-8">
              Enter your email and we&rsquo;ll send you a link to choose a new password.
            </p>

            {error && (
              <div className="bg-error/10 border border-error/30 text-error px-4 py-3 rounded-lg mb-6">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-text-light mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="aura-input"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-champagne flex items-center justify-center gap-2 py-4"
              >
                {loading ? <span className="animate-pulse">Sending...</span> : (<>Send reset link<ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>

            <p className="text-center text-text-muted mt-6">
              Remembered it?{' '}
              <Link to="/login" className="text-champagne font-medium hover:underline">Sign in</Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
